import { z } from 'zod'
import { useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react'
import { useQueryState } from 'nuqs'
import { downloadCsv, toCsvContent, parseAsSearchQuery } from '@util'
import {
  ACCESS_STATUSES,
  COMPANY_KINDS,
  AccessStatusSchema,
  CompanyIndustrySchema,
  CompanyKindSchema,
  capitalize,
  getCreatedAt,
  isEmpty,
  isIncludedIn,
  isNotEmpty,
  isNotDefined,
  type AccessStatus,
  type CompanyIndustry,
  type CompanyKind,
  type TinyCompany,
} from '@terros/common'
import { getCoreRowModel, getSortedRowModel, useReactTable, type SortingState } from '@tanstack/react-table'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  CloseButton,
  Combobox,
  Group,
  Input,
  PillsInput,
  Select,
  Stack,
  Text,
  TextInput,
  useCombobox,
} from '@mantine/core'
import { useStoredQuery } from '@hooks'
import {
  COMPANY_INDUSTRY_OPTIONS,
  getCompanyIndustryLabels,
  toShowingCountText,
  useCompanies,
  useProfile,
} from '@front/model'
import {
  ButtonAdd,
  ButtonDownloadFile,
  MultiSelectFilterOption,
  NotReady,
  Page,
  PermissionTooltip,
  TTable,
  ToggleColumns,
  useMultiSelectFilterState,
} from '@components'
import { useCompanyColumnState } from './useCompanyColumnState'
import { PINNED_COMPANY_COLUMN_IDS } from './companyTableColumns'
import { useCompanyColumns } from './companyColumns'

const ALL = 'all'
const UNKNOWN = 'unknown'
type StatusFilter = AccessStatus | typeof ALL
type IndustryFilter = CompanyIndustry | typeof UNKNOWN

const KindFilterSchema = z.array(CompanyKindSchema)
const StatusFilterSchema = z.union([AccessStatusSchema, z.literal(ALL)])
const IndustryFilterSchema = z.array(z.union([CompanyIndustrySchema, z.literal(UNKNOWN)]))

const KIND_OPTIONS = COMPANY_KINDS.map((kind) => ({ label: capitalize(kind), value: kind }))
const ACCESS_STATUS_OPTIONS = ACCESS_STATUSES.map((status) => ({ label: capitalize(status), value: status }))
const STATUS_OPTIONS = [{ label: 'All Statuses', value: ALL }, ...ACCESS_STATUS_OPTIONS]
const INDUSTRY_OPTIONS: Array<{ label: string; value: IndustryFilter }> = [
  { label: 'Unknown', value: UNKNOWN },
  ...COMPANY_INDUSTRY_OPTIONS,
]
const MULTI_SELECT_FILTER_BEHAVIOR = { emptyBehavior: 'all', allValueBehavior: 'empty' } as const

function Companies(): ReactNode {
  const { data, isLoading, error, refetch, isFetching } = useCompanies()
  const { user, isTantalim } = useProfile()
  const navigate = useNavigate()
  const [filter, setFilter] = useQueryState('q', parseAsSearchQuery.withDefault(''))
  const { value: kindFilter, setValue: setKindFilter } = useStoredQuery<CompanyKind[]>({
    defaultValue: [],
    storedKey: 'Companies|kindFilter',
    urlKey: 'companyKinds',
    schema: KindFilterSchema,
  })
  const { value: statusFilter, setValue: setStatusFilter } = useStoredQuery<StatusFilter>({
    defaultValue: ALL,
    storedKey: 'Companies|statusFilter',
    urlKey: 'companyStatus',
    schema: StatusFilterSchema,
  })
  const { value: industryFilter, setValue: setIndustryFilter } = useStoredQuery<IndustryFilter[]>({
    defaultValue: [],
    storedKey: 'Companies|industryFilter',
    urlKey: 'companyIndustries',
    schema: IndustryFilterSchema,
  })
  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }])
  const { columns: toggleableColumns, selectedColumns, setSelectedColumns } = useCompanyColumnState()
  const allColumns = useCompanyColumns({ canEdit: isTantalim })
  const columns = useMemo(() => {
    const selected = new Set<string>(selectedColumns)
    return allColumns.filter((column) => {
      const { id } = column
      if (!id) return true
      return PINNED_COMPANY_COLUMN_IDS.includes(id) || selected.has(id)
    })
  }, [allColumns, selectedColumns])
  const filtered = useMemo(
    () => filterCompanies(data ?? [], { filter, kindFilter, statusFilter, industryFilter }),
    [data, filter, kindFilter, statusFilter, industryFilter]
  )
  const table = useReactTable({
    data: filtered,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (company) => company.companyId,
    onSortingChange: setSorting,
    enableMultiSort: false,
    state: {
      sorting,
      columnPinning: {
        left: ['rowNumber', 'name'],
      },
    },
  })
  if (isLoading || error || !data) return <NotReady data={data} error={error} id='companies' isLoading={isLoading} />

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={refetch} title='Companies'>
        <PermissionTooltip permissionType='company:add' targetId={user?.userId}>
          <ButtonAdd component={Link} to='/companies/add'>
            Add Company
          </ButtonAdd>
        </PermissionTooltip>
      </Page.Heading>
      <Page.FunctionBar>
        <Stack w='100%' gap='sm'>
          <Group justify='space-between' gap='sm'>
            <Group gap='sm'>
              <TextInput
                autoFocus
                placeholder='Filter...'
                value={filter}
                w={260}
                onChange={(e) => setFilter(e.currentTarget.value)}
              />
              <Text c='dimmed' size='sm'>
                {toShowingCountText(filtered.length, data.length, 'company', 'companies')}
              </Text>
            </Group>
            <Group gap='sm'>
              <ToggleColumns
                columns={toggleableColumns}
                selectedColumns={selectedColumns}
                setSelectedColumns={setSelectedColumns}
              />
              <ButtonDownloadFile
                disabled={isEmpty(filtered)}
                tooltip='Download companies CSV'
                onClick={() => {
                  const displayed = table.getRowModel().rows.map(({ original }) => original)
                  downloadCsv(toCompaniesCsv(displayed), 'companies')
                }}
              />
            </Group>
          </Group>
          <Group gap='sm'>
            <CompanyMultiSelectFilter
              data={KIND_OPTIONS}
              value={kindFilter}
              onChange={setKindFilter}
              placeholder='All kinds'
              pluralLabel='kinds'
            />
            <Select
              data={STATUS_OPTIONS}
              placeholder='All statuses'
              value={statusFilter === ALL ? null : statusFilter}
              w={160}
              onChange={(value) => setStatusFilter(toStatusFilter(value))}
            />
            <CompanyMultiSelectFilter
              data={INDUSTRY_OPTIONS}
              value={industryFilter}
              onChange={setIndustryFilter}
              placeholder='All industries'
              pluralLabel='industries'
            />
          </Group>
        </Stack>
      </Page.FunctionBar>
      <Page.Content>
        <TTable
          sortable
          data={filtered}
          emptyIcon='business'
          isLoading={isLoading}
          table={table}
          total={filtered.length}
          onRowClick={(company) => void navigate({ to: `/companies/${company.companyId}` })}
        />
      </Page.Content>
    </Page>
  )
}

const toStatusFilter = (value: string | null): StatusFilter => (value && isStatusFilter(value) ? value : ALL)

type CompanyMultiSelectFilterProps<T extends string> = {
  data: Array<{ label: string; value: T }>
  value: T[]
  onChange: Dispatch<SetStateAction<T[]>>
  placeholder: string
  pluralLabel: string
}

function CompanyMultiSelectFilter<T extends string>({
  data,
  value,
  onChange,
  placeholder,
  pluralLabel,
}: CompanyMultiSelectFilterProps<T>): ReactNode {
  const combobox = useCombobox({ onDropdownClose: () => combobox.resetSelectedOption() })
  const allValues = useMemo(() => data.map((option) => option.value), [data])
  const { isSelected, toggle, selectRow, selectAll, getRowAction, doesToggleSelectAll } = useMultiSelectFilterState({
    allValues,
    selectedValues: value,
    setSelectedValues: onChange,
    behavior: MULTI_SELECT_FILTER_BEHAVIOR,
  })
  const displayValue = toMultiSelectFilterLabel(value, data, placeholder, pluralLabel)

  return (
    <Combobox
      store={combobox}
      position='bottom-start'
      width={360}
      onOptionSubmit={(submitted) => {
        const option = data.find(({ value }) => value === submitted)
        if (option) selectRow(option.value)
      }}
    >
      <Combobox.Target>
        <PillsInput
          pointer
          w={220}
          rightSection={
            isEmpty(value) ? (
              <Combobox.Chevron />
            ) : (
              <CloseButton
                size='sm'
                aria-label={`Check all ${pluralLabel}`}
                onClick={(event) => {
                  event.stopPropagation()
                  selectAll()
                }}
              />
            )
          }
          rightSectionPointerEvents='all'
          onClick={() => combobox.toggleDropdown()}
        >
          {isEmpty(value) ? <Input.Placeholder>{displayValue}</Input.Placeholder> : displayValue}
        </PillsInput>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options>
          {data.map(({ label, value: optionValue }) => (
            <MultiSelectFilterOption
              key={optionValue}
              label={label}
              value={optionValue}
              isSelected={isSelected(optionValue)}
              doesToggleSelectAll={doesToggleSelectAll(optionValue)}
              onToggle={() => toggle(optionValue)}
              rowAction={getRowAction(optionValue)}
            />
          ))}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

export function toMultiSelectFilterLabel<T extends string>(
  value: T[],
  data: Array<{ label: string; value: T }>,
  placeholder: string,
  pluralLabel: string
): string {
  if (isEmpty(value)) return placeholder
  if (value.length === 1) return data.find((option) => option.value === value[0])?.label ?? value[0]
  return `${value.length} ${pluralLabel}`
}

const getAccessStatus = (company: TinyCompany): AccessStatus => company.accessStatus ?? 'active'

export const filterCompanies = (
  companies: TinyCompany[],
  {
    filter,
    kindFilter,
    statusFilter,
    industryFilter,
  }: {
    filter: string
    kindFilter: CompanyKind[]
    statusFilter: StatusFilter
    industryFilter: IndustryFilter[]
  }
): TinyCompany[] => {
  const query = filter.toLowerCase()
  return companies.filter((company) => {
    if (isNotEmpty(kindFilter) && !kindFilter.includes(company.kind)) return false
    if (statusFilter !== ALL && getAccessStatus(company) !== statusFilter) return false
    if (!matchesIndustryFilter(company, industryFilter)) return false
    if (!query) return true
    const compareList = [
      company.name,
      company.companyId,
      company.salesforceAccountId,
      company.kind,
      getAccessStatus(company),
      getCompanyIndustryLabels(company.industries),
    ]
    return compareList.some((item) => isIncludedIn(item, query))
  })
}

const matchesIndustryFilter = (company: TinyCompany, industryFilter: IndustryFilter[]): boolean => {
  if (isEmpty(industryFilter)) return true
  if (industryFilter.includes(UNKNOWN) && isEmpty(company.industries)) return true
  return company.industries?.some((industry) => industryFilter.includes(industry)) ?? false
}

export const toCompaniesCsv = (companies: TinyCompany[]): string => {
  return toCsvContent(
    ['Company', 'Kind', 'Status', 'Industries', 'Max Users', 'Created', 'Company ID', 'Salesforce Account ID'],
    companies.map((company) => [
      company.name,
      capitalize(company.kind),
      capitalize(getAccessStatus(company)),
      getCompanyIndustryLabels(company.industries) ?? 'Unknown',
      company.maxUsers,
      toCreatedAtCsvValue(company),
      company.companyId,
      company.salesforceAccountId,
    ])
  )
}

const toCreatedAtCsvValue = (company: TinyCompany): string | undefined => {
  const createdAt = getCreatedAt(company)
  return isNotDefined(createdAt) ? undefined : new Date(createdAt).toISOString()
}

const isStatusFilter = (value: string): value is StatusFilter =>
  value === ALL || ACCESS_STATUSES.includes(value as AccessStatus)

export const Route = createFileRoute('/companies/')({
  component: Companies,
})
